import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, Renderer2, inject, signal, viewChild, viewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { ConfigService, CustomTopic, DifficultyLevel, GameEngineService, GameService, InternationalizationService, PlaySoundsService, SessionService, TransitionService, User } from '@app/core/services';
import { JeopardyQuestion, JeopardyRound, JeopardyUser } from '@app/shared/graphql/types';
import { BehaviorSubject, Subject, Subscription, timer } from 'rxjs';
import { concatMap, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';

type CustomRound = JeopardyRound & {
  noAnswerSelected: boolean;
  active?: boolean;
  wrongAnswer: boolean;
  answerSkipped: boolean;
  hasAnswer: boolean;
  correctAnswer: boolean;
}

const pickRandom = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

const SCROLL_STEP_SIZE = 3;

@Component({
  selector: 'app-select-topic',
  templateUrl: './select-topic.component.html',
  styleUrls: ['./select-topic.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Default,
})
export class SelectTopicComponent implements OnInit, OnDestroy {
  topics: CustomTopic[];
  topicSelected: CustomTopic;
  selectedToken: DifficultyLevel | null;
  disabledTokens: number[];
  difficultyLevels: DifficultyLevel[];
  currentRound: JeopardyRound;
  disabledTopics: string[];
  score: number;
  currentUser: User;
  roundIndex: number;
  progressWidth: number;
  translateX: string;
  gameLoaded: boolean;
  isWheelSpinning: boolean;
  gameRounds = signal<CustomRound[]>([]);
  roundsNumber = signal<number>(0);
  soundsOff = signal(true);
  musicOff = signal(true);

  i18n = inject(InternationalizationService);
  gameEngineService = inject(GameEngineService);

  private wheel = viewChild<ElementRef>('wheel');
  private roundsContainer = viewChild<ElementRef>('roundsContainer');
  private lightbulb = viewChildren<ElementRef>('lightbulb');//<QueryList<ElementRef>>('lightbulb');

  private odd: boolean;
  private glow: number;
  private iGlow: number;
  private goBtnActive: boolean;
  private gameId: number;
  private disabledQuestions: JeopardyQuestion[];
  private expiringRound$: Subscription;
  private beforeRoundStarted: Subscription;
  private roundStartSub: Subscription;
  private roundEndSub: Subscription;
  private userIdleSub: Subscription;
  private userDisconnectedSub: Subscription;
  private inactiveGameSub: Subscription;
  private realAngle: number;
  private connectedPlayer: JeopardyUser;
  private subscriptions: Subscription;
  private showSelectedTopicTimeout: any;

  private initGame$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private loadDifficultyLevels$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private loadTopics$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private topicsLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private destroy$ = new Subject();

  private renderer = inject(Renderer2);
  private router = inject(Router);
  private gameService = inject(GameService);
  private sessionService = inject(SessionService);
  private soundsService = inject(PlaySoundsService);
  private transitionService = inject(TransitionService);
  private playSoundsService = inject(PlaySoundsService);
  private configService = inject(ConfigService);
  ngOnInit(): void {
    this.InitMusicAndSounds()

    this.showSelectedTopicTimeout = null;
    this.isWheelSpinning = false;
    this.subscriptions = new Subscription();
    this.topics = [];
    this.gameLoaded = false;

    this.connectedPlayer = null;
    this.roundIndex = 0;
    this.progressWidth = 10;
    this.realAngle = 0;

    if (!this.soundsService.soundsOff) {
      this.soundsService.playSound('page-transition');
    }

    this.disabledQuestions = [];
    this.topicSelected = null;
    this.currentRound = null;
    this.gameId = +this.gameService.getGameId();

    this.odd = false;
    this.goBtnActive = false;

    this.translateX = `translateX(0)`;
    this.disabledTokens = [];
    this.disabledTopics = [];

    this.currentUser = this.sessionService.getUser();

    this.initGame$.pipe(
      filter(load => load),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loadDifficultyLevels$?.next(true);

      if (!this.gameEngineService.gameId) {
        this.loadTopics$?.next(true);
      } else {
        this.gameEngineService.reloadGameData()
          .pipe(
            tap(() => {
              this._initGame();
            }),
            takeUntil(this.destroy$)
          )
          .subscribe();
      }
    });

    this.loadDifficultyLevels$
      .pipe(
        filter(load => load),
        switchMap(() => this.gameService.getDifficultyLevels()),
        tap(levels => {
          this.difficultyLevels = levels;
          this.loadDifficultyLevels$?.next(false);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.loadTopics$.pipe(
      filter(loaded => loaded),
      switchMap(() => this.gameEngineService.loadTopics()),
      takeUntil(this.destroy$)
    ).subscribe((topics) => {
      if (topics.length) {
        this.topicsLoaded$?.next(true);
      }
    });

    this.topicsLoaded$.pipe(
      filter(loaded => loaded),
      switchMap(() => this.gameEngineService.init(this.gameId)),
      takeUntil(this.destroy$)
    )
      .subscribe(() => {
        this._initGame();
      });

    this.initGame$?.next(true);
  }

  loadPreviousTopic(): void {
    if (this.gameEngineService.selectedTopic) {
      this.realAngle = 360 - this.gameEngineService.selectedTopic.end - 75;
    }

    this.activateGoButton();
    setTimeout(() => {
      this.renderer.setStyle(this.wheel().nativeElement, 'transform', `rotate(${this.realAngle}deg)`);
    });
  }

  private _initGame(): void {
    if (this.gameEngineService.roundStarted) {
      this.router.navigate(['/question']);
      return;
    }

    this.topics = this.gameEngineService.topics;
    this.setDisabledTopics();

    const ROUND_WIDTH = 3;
    const ACTIVE_ROUND_WIDTH = 1.3 * ROUND_WIDTH;
    const SPACER_WIDTH = 2;

    this.startSubscriptions(this.gameId);

    this.connectedPlayer = this.gameEngineService.connectedPlayer;
    this.score = this.connectedPlayer.score || this.gameEngineService.gameEngineDetails.settingDefaultScore;
    this.currentRound = this.gameEngineService.currentRound;

    this.gameRounds.update(() => {
      let gRounds = this.gameEngineService.gameRounds;
      if (!gRounds || gRounds?.length <= 0) {
        this.gameEngineService.loadGameRounds().subscribe();
        gRounds = this.gameEngineService.gameRounds;
      }

      return gRounds?.map(round => this.toCustomRound(round));
    });
    this.roundsNumber.update(() => this.gameRounds().length);

    this.progressWidth = (this.roundsNumber() - 1) * ROUND_WIDTH + ACTIVE_ROUND_WIDTH + (this.roundsNumber() - 1) * SPACER_WIDTH + 5.5;
    this.roundIndex = this.currentRound.roundNumber - 1;

    if (this.currentRound.roundNumber > 3) {
      this.translateX = `translateX(-${(this.roundIndex - 1) * 4}em)`;
    }

    if (this.connectedPlayer.userId !== this.currentRound.activeUser?.userId) {
      this.transitionService.showGameLoader(`Waiting for ${this.currentRound.activeUser?.name} to select topic`);
    }

    this.gameLoaded = true;

    this.loadPreviousTopic();
  }

  public canSpin(): boolean {
    return !this.isWheelSpinning && !this.topicSelected && !this.selectedToken && this.gameEngineService.active;
  }

  private getRoundAnswer(round: JeopardyRound): any {
    if (round.jeopardyUserAnswers.length === 0) {
      return null;
    }

    return round.jeopardyUserAnswers.filter(answer => answer.roundId === round.roundId && answer.userId === this.connectedPlayer.userId)[0];
  }

  private toCustomRound(round: JeopardyRound): CustomRound {
    const answer = this.getRoundAnswer(round);
    const customRound = {
      ...round,
      hasAnswer: !!answer,
      answerSkipped: !!answer && answer.skip,
      correctAnswer: !!answer && answer.selectedAnswerIndex === answer.trueAnswerIndex,
      wrongAnswer: !!answer && answer.selectedAnswerIndex !== answer.trueAnswerIndex && !answer.skip && answer.selectedAnswerIndex !== 99,
      active: !answer && round.roundId === this.currentRound.roundId,
      noAnswerSelected: !!answer && answer.selectedAnswerIndex === 99,
    };
    return customRound;
  }

  private groupByTopic<T>(list: JeopardyQuestion[], keyGetter: (item: JeopardyQuestion) => T) {
    const map = new Map<T, JeopardyQuestion[]>();
    list.forEach((item) => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });
    return map;
  }

  private startRound(ev: MouseEvent) {
    ev.preventDefault();
    if (!this.goBtnActive || !this.gameEngineService.active) {
      return;
    }

    this.transitionService.showGameLoader(this.i18n.str('challengeu.msg_loading_question', 'Loading question...')());
    const getRoundQuestion$ = this.gameService.getRoundQuestionQuery(this.gameId, this.topicSelected.id, this.selectedToken.points);

    getRoundQuestion$.pipe(
      concatMap((question) => {
        return this.gameService.setRoundQuestion(this.gameId, this.currentRound?.activeUser.userId, question[0]?.questionId);
      }),
      tap(() => {
        this.gameEngineService.lastTopic = this.topicSelected;
        this.gameEngineService.difficulty = this.selectedToken;

        this.sessionService.setLastTopic(this.topicSelected);

        timer(1000).pipe(
          take(1),
          tap(() => {
            this.router.navigate(['question']);
          })
        ).subscribe();

      }),
      take(1),
    ).subscribe();
  }

  private addFlicker(template: boolean) {
    this.odd = template;

    this.lightbulb().forEach((_, i) => {
      _?.nativeElement.classList.add('off');
      _?.nativeElement.classList.remove('on');


      if (this.odd) {
        if (i % 2 === 0) {
          _?.nativeElement.classList.remove('off');
          _?.nativeElement.classList.add('on');
        }
      } else {
        if (i % 2 === 1) {
          _?.nativeElement.classList.remove('off');
          _?.nativeElement.classList.add('on');
        }
      }
    });
  }

  private flickerTurnOff() {
    this.lightbulb().forEach((el) => {
      el?.nativeElement.classList.add('off');
      el?.nativeElement.classList.remove('on');
    });
    window.clearInterval(this.iGlow);

  }

  private flickerOddEven() {
    const delay = 500;

    this.iGlow = window.setInterval(() => {
      this.lightbulb().forEach((el) => {
        el?.nativeElement.classList.add('off');
        el?.nativeElement.classList.remove('on');

        this.addFlicker(this.odd);
      });

      this.odd = !this.odd;
    }, delay);
  }

  private activateGoButton() {
    this.goBtnActive = !!(this.topicSelected && this.selectedToken);
  }

  public selectToken(ev: MouseEvent, token: DifficultyLevel) {
    ev.preventDefault();
    if (this.disabledTokens.includes(token.points) || !this.gameEngineService.active || this.isWheelSpinning || !this.topicSelected) {
      return;
    }

    if (this.selectedToken && this.selectedToken.points === token.points) {
      this.selectedToken = null;
    } else {
      this.selectedToken = token;
    }

    setTimeout(() => {
      this.activateGoButton();
      this.startRound(ev);
    }, 3000);
  }

  ngOnDestroy() {
    if (this.iGlow) {
      window.clearInterval(this.iGlow);
    }
    if (this.glow) {
      window.clearInterval(this.glow);
    }

    this.soundsService.stopSound();
    this.subscriptions.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  public topicIconUrl(topic: CustomTopic): string {
    return this.configService.config().UmbracoApiBaseUrl + topic.icon;
  }

  private setDisabledTokens() {
    if (this.topicSelected) {
      this.disabledTokens = this.disabledQuestions.filter(e => e.topicId === this.topicSelected.id).map(e => e.points);
    } else if (this.gameEngineService.selectedTopic) {
      this.disabledTokens = this.disabledQuestions.filter(e => e.topicId === this.gameEngineService.selectedTopic.id).map(e => e.points);
    }
  }

  private setDisabledTopics() {
    const disabledQuestions$ = this.gameService.getDisabledQuestions(this.gameId);

    disabledQuestions$.pipe(
      tap(disabledQuestions => {
        this.disabledQuestions = disabledQuestions;

        const groupedTopics = this.groupByTopic(this.disabledQuestions, (q) => q.topicId);

        for (const [key, value] of groupedTopics) {
          if (value.length === 5) {
            this.disabledTopics.push(key);
          }
        }

        this.setDisabledTokens();
      }),
      take(1)
    ).subscribe();
  }

  private startSubscriptions(gameId: number) {
    this.expiringRound$ = this.gameService.getExpiringRoundSubscription(gameId).subscribe(() => {
      // TODO: hurry user to press spin
    });

    this.subscriptions.add(this.expiringRound$);
    // before round started
    this.beforeRoundStarted = this.gameService.getBeforeRoundStartedSubscription(gameId).subscribe(_ => {
      console.log('before round started');
    });

    this.subscriptions.add(this.beforeRoundStarted);

    // round started
    this.roundStartSub = this.gameService.getRoundStartedSubscription(gameId).subscribe(_ => {

      this.transitionService.showGameLoader(this.i18n.str('challengeu.msg_loading_question', 'Loading question...')());

      setTimeout(() => {
        const getGameRound = this.gameService.getGameRound(gameId).subscribe(round => {
          const lastTopicId = round.question.topicId;

          const lastTopic = this.topics.find((topic) => topic.id === lastTopicId);
          this.sessionService.setLastTopic(lastTopic);

          this.router.navigate(['question']);

          setTimeout(() => {
            this.transitionService.hideGameLoader();
          }, 500);
        });

        this.subscriptions.add(getGameRound);
      }, 500);
    });

    this.subscriptions.add(this.roundStartSub);

    // round ended
    this.roundEndSub = this.gameService.getRoundEndedSubscription(gameId).subscribe(_ => {
      console.log('round ended');
      this.router.navigate(['/select-topic']);
    });

    this.subscriptions.add(this.roundEndSub);

    // userConnected
    // this.gameService.getUserConnectedSubscription(gameId).subscribe(_ => console.log('user connected'));

    // user idle
    this.userIdleSub = this.gameService.getUserIdleSubscription(gameId).subscribe(_ => {
      console.log('user in idle');
      // this.gameService.userIdle.next(true);
    });

    this.subscriptions.add(this.userIdleSub);

    // user disconnected
    this.userDisconnectedSub = this.gameService.getUserIdleDisconnectedSubscription(gameId).subscribe(_ => {
      console.log('user disconnected');
      // this.gameService.userIdle.next(false);
      // this.gameService.userDisconnected.next(true);
    });

    this.subscriptions.add(this.userDisconnectedSub);

    // user updated
    // this.gameService.getUserScoreUpdatedSubscription(gameId).subscribe(_ => console.log('user score updated'));

    // inactive game
    this.inactiveGameSub = this.gameService.getGameInactivatedSubscription(gameId).subscribe(_ => {
      // this.gameService.userIdle.next(false);
      console.log('game ended');
      // this.inactiveGame = true;

      // this.finishGame();

      this.gameEngineService.clearGame();
      this.gameService.finishGame();
      this.sessionService.clearSession();
    });

    this.subscriptions.add(this.inactiveGameSub);
  }

  public scrollRight(event: Event) {
    event.preventDefault();
    if (this.roundIndex === this.roundsNumber() - 1) {
      return;
    }

    if (this.roundIndex + SCROLL_STEP_SIZE > this.roundsNumber() - SCROLL_STEP_SIZE) {
      this.roundsContainer().nativeElement.style.transform = `translateX(-${(this.roundsNumber() - SCROLL_STEP_SIZE) * 4}em)`;

      this.roundIndex = this.roundsNumber() - 1;
    } else {
      this.roundIndex += SCROLL_STEP_SIZE;
      this.roundsContainer().nativeElement.style.transform = `translateX(-${this.roundIndex * 4}em)`;
    }
  }

  public scrollLeft(event: Event) {
    event.preventDefault();
    if (this.roundIndex === 0) {
      return;
    }

    if (this.roundIndex - SCROLL_STEP_SIZE < 0) {
      this.roundIndex = 0;
    } else {
      this.roundIndex -= SCROLL_STEP_SIZE;
    }

    let translate = this.roundIndex * 4;
    this.roundsContainer().nativeElement.style.transform = `translateX(-${translate}em)`;
  }

  public scrollRightMobile(event: Event) {
    event.preventDefault();

    if (this.roundIndex === this.roundsNumber() - 1) {
      return;
    }
    this.roundIndex++;
  }

  public scrollLeftMobile(event: Event) {
    event.preventDefault();
    if (this.roundIndex === 0) {
      return;
    }
    this.roundIndex--;
  }

  public newRandomSpin(event: Event) {
    event?.preventDefault();

    if (!this.canSpin()) {
      return;
    }

    if (this.showSelectedTopicTimeout) {
      clearTimeout(this.showSelectedTopicTimeout)
    }
    this.isWheelSpinning = true;

    const turns = 5 * 360;
    const finishedTopicIds = this.getFinishedTopics() ?? [];
    const topics = [
      ...this.topics
        .filter((topic) => !finishedTopicIds.includes(topic.id))
        .filter((topic) => topic.id !== this.topicSelected?.id),
    ];

    const topic = pickRandom(topics);
    console.log('----->', topic);


    if (!this.topicSelected) {
      this.realAngle = 360 - topic?.end - 75;
      this.realAngle = this.realAngle + turns;
    } else {
      this.realAngle += +(this.topicSelected.end - topic.end);
      this.realAngle += turns;
    }

    this.topicSelected = null;
    this.selectedToken = null;
    this.activateGoButton();

    this.renderer.setStyle(this.wheel().nativeElement, 'transform', `rotate(${this.realAngle}deg)`);
    this.renderer.setStyle(this.wheel().nativeElement, 'transition', `transform 3s`);
    if (!this.iGlow) {
      this.flickerOddEven();
    }

    this.showSelectedTopicTimeout = setTimeout(() => {
      setTimeout(() => {
        this.isWheelSpinning = false;
        this.gameEngineService.selectedTopic = this.topicSelected = topic;

        this.flickerTurnOff();

        this.setDisabledTokens();
      }, 500);
    }, 2500);
  }

  private getFinishedTopics(): string[] {
    const disabledQuestionsByTopic = this.disabledQuestions.reduce((acc: { [key: string]: number }, item) => {
      const key = item.topicId;
      const value = 1;
      if (!acc[key]) {
        acc[key] = 0;
      }
      acc[key] += value;
      return acc;
    }, {})

    return Object.keys(disabledQuestionsByTopic).filter(key => disabledQuestionsByTopic[key] === this.difficultyLevels.length);
  }

  InitMusicAndSounds() {
    const session = this.sessionService.getSession();
    this.soundsOff.set(session?.soundsOff ?? false);
    this.musicOff.set(session?.musicOff ?? false);
    this.sessionService.setSoundsOff(this.soundsOff());

    this.sessionService.setMusicOff(this.musicOff());
    this.musicOff.set(this.playSoundsService.musicOff);
    if (!this.playSoundsService.musicOff) {
      this.playSoundsService.playMusic('settings');
    } else {
      this.playSoundsService.pauseMusic();
    }

  }

  doMuteMusic(ev?: MouseEvent) {
    ev?.preventDefault();

    this.playSoundsService.musicOff = !this.playSoundsService.musicOff;
    this.sessionService.setMusicOff(this.playSoundsService.musicOff);
    this.musicOff.set(this.playSoundsService.musicOff);

    if (!this.playSoundsService.musicOff) {
      this.playSoundsService.playMusic('settings');
    } else {
      this.playSoundsService.pauseMusic();
    }
  }

  setSoundsFX(ev?: Event) {
    ev?.preventDefault();
    this.playSoundsService.soundsOff = !this.playSoundsService.soundsOff;
    this.sessionService.setSoundsOff(this.playSoundsService.soundsOff);
    this.soundsOff.set(this.playSoundsService.soundsOff);
  }

}
